import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { firestoreService } from '@/services/firestoreService';

interface Analytics {
  totalUsers: number;
  totalTranscripts: number;
  totalFilings: number;
  totalAnalyses: number;
  totalNews: number;
  totalRankings: number;
  totalCreditTransactions: number;
  totalCreditsUsed: number;
  mostAnalyzedCompanies: Array<{ companyName: string; count: number }>;
  topTranscripts: Array<{ transcriptId: string; companyName: string; quarter: string; count: number }>;
  recentActivity: Array<{
    type: string;
    description: string;
    timestamp: any;
  }>;
  dailyActiveUsers: number;
  estimatedOpenAICost: number;
}

export const Analytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | 'all'>('30d');

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      
      const now = new Date();
      const timeRangeDate = new Date();
      if (timeRange === '7d') {
        timeRangeDate.setDate(now.getDate() - 7);
      } else if (timeRange === '30d') {
        timeRangeDate.setDate(now.getDate() - 30);
      } else {
        timeRangeDate.setTime(0); // All time
      }

      // Count users from credits collection (every user has a credits document)
      // Use Promise.allSettled to handle individual failures gracefully
      const results = await Promise.allSettled([
        firestoreService.getDocuments('credits', []).catch(() => []),
        firestoreService.getDocuments('transcripts', []),
        firestoreService.getDocuments('filings', []),
        firestoreService.getDocuments('transcriptAnalyses', []),
        firestoreService.getDocuments('filingAnalyses', []),
        firestoreService.getDocuments('news', []),
        firestoreService.getDocuments('rankings', []),
        firestoreService.getDocuments('creditTransactions', []).catch(() => []),
      ]);

      const [credits, transcripts, filings, transcriptAnalyses, filingAnalyses, news, rankings, creditTransactions] = results.map((result, index) => {
        if (result.status === 'fulfilled') {
          return result.value;
        } else {
          console.error(`Failed to load collection ${index}:`, result.reason);
          return []; // Return empty array on error
        }
      });

      // Calculate most analyzed companies
      const companyAnalysisCount: Record<string, { name: string; count: number }> = {};
      transcriptAnalyses.forEach((analysis: any) => {
        const companyId = analysis.companyId || 'unknown';
        const companyName = analysis.companyName || 'Unknown';
        if (!companyAnalysisCount[companyId]) {
          companyAnalysisCount[companyId] = { name: companyName, count: 0 };
        }
        companyAnalysisCount[companyId].count++;
      });

      const mostAnalyzedCompanies = Object.values(companyAnalysisCount)
        .sort((a, b) => b.count - a.count)
        .slice(0, 10)
        .map((item) => ({ companyName: item.name, count: item.count }));

      // Calculate top transcripts (by number of times analyzed - for now, count analyses)
      // In a real system, you'd track views/analyses separately
      const transcriptCount: Record<string, { companyName: string; quarter: string; count: number }> = {};
      transcriptAnalyses.forEach((analysis: any) => {
        const transcriptId = analysis.transcriptId || 'unknown';
        if (!transcriptCount[transcriptId]) {
          transcriptCount[transcriptId] = {
            companyName: analysis.companyName || 'Unknown',
            quarter: analysis.quarter || 'Unknown',
            count: 0,
          };
        }
        transcriptCount[transcriptId].count++;
      });

      const topTranscripts = Object.entries(transcriptCount)
        .map(([transcriptId, data]) => ({ transcriptId, ...data }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // Filter credit transactions by time range
      const filteredCreditTransactions = creditTransactions.filter((tx: any) => {
        const txDate = tx.timestamp?.toDate?.() || tx.timestamp;
        if (!txDate) return false;
        return new Date(txDate) >= timeRangeDate;
      });

      // Calculate credits used (sum of negative transactions)
      const totalCreditsUsed = filteredCreditTransactions
        .filter((tx: any) => tx.amount < 0)
        .reduce((sum: number, tx: any) => sum + Math.abs(tx.amount), 0);

      // Estimate OpenAI costs
      // Rough estimate: $0.03 per transcript analysis, $0.05 per filing analysis, $0.01 per copilot question
      // Filter analyses by time range
      const filteredTranscriptAnalyses = transcriptAnalyses.filter((analysis: any) => {
        const analysisDate = analysis.createdAt?.toDate?.() || analysis.createdAt;
        if (!analysisDate) return false;
        return new Date(analysisDate) >= timeRangeDate;
      });
      const filteredFilingAnalyses = filingAnalyses.filter((analysis: any) => {
        const analysisDate = analysis.createdAt?.toDate?.() || analysis.createdAt;
        if (!analysisDate) return false;
        return new Date(analysisDate) >= timeRangeDate;
      });
      const transcriptAnalysisCount = filteredTranscriptAnalyses.length;
      const filingAnalysisCount = filteredFilingAnalyses.length;
      const copilotQuestions = filteredCreditTransactions.filter((tx: any) => tx.action === 'copilotQuestion').length;
      const estimatedOpenAICost = 
        (transcriptAnalysisCount * 0.03) + 
        (filingAnalysisCount * 0.05) + 
        (copilotQuestions * 0.01);

      // Calculate daily active users (users who used credits in time range)
      const activeUserIds = new Set(
        filteredCreditTransactions.map((tx: any) => tx.userId).filter(Boolean)
      );
      const dailyActiveUsers = activeUserIds.size;

      // Try to load system logs, but don't fail if permission denied
      let systemLogs: any[] = [];
      try {
        systemLogs = await firestoreService.getDocuments('systemLogs', [
          firestoreService.orderBy('timestamp', 'desc'),
          firestoreService.limit(20),
        ]);
      } catch (error: any) {
        if (error?.code === 'permission-denied') {
          console.warn('Permission denied for systemLogs. Admin access may be required.');
        } else {
          console.error('Failed to load system logs:', error);
        }
      }

      setAnalytics({
        totalUsers: credits.length,
        totalTranscripts: transcripts.length,
        totalFilings: filings.length,
        totalAnalyses: transcriptAnalyses.length + filingAnalyses.length,
        totalNews: news.length,
        totalRankings: rankings.length,
        totalCreditTransactions: filteredCreditTransactions.length,
        totalCreditsUsed,
        mostAnalyzedCompanies,
        topTranscripts,
        recentActivity: systemLogs.map((log: any) => ({
          type: log.type || 'info',
          description: log.message || log.context || 'Activity',
          timestamp: log.timestamp,
        })),
        dailyActiveUsers,
        estimatedOpenAICost,
      });
    } catch (error) {
      console.error('Failed to load analytics:', error);
      // Set default values on complete failure
      setAnalytics({
        totalUsers: 0,
        totalTranscripts: 0,
        totalFilings: 0,
        totalAnalyses: 0,
        totalNews: 0,
        totalRankings: 0,
        totalCreditTransactions: 0,
        totalCreditsUsed: 0,
        mostAnalyzedCompanies: [],
        topTranscripts: [],
        recentActivity: [],
        dailyActiveUsers: 0,
        estimatedOpenAICost: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blazerex-green"></div>
      </div>
    );
  }

  if (!analytics) {
    return <div>Failed to load analytics</div>;
  }

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="flex items-center justify-between">
        <h2 className="sub-header text-2xl">System Analytics</h2>
        <div className="flex gap-2">
          {(['7d', '30d', 'all'] as const).map((range) => (
            <Button
              key={range}
              variant={timeRange === range ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setTimeRange(range)}
            >
              {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : 'All Time'}
            </Button>
          ))}
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <div className="text-center">
            <p className="body-text text-sm text-gray-600 mb-1">Total Users</p>
            <p className="h1 text-4xl text-blazerex-green">{analytics.totalUsers}</p>
            <p className="body-text text-xs text-gray-500 mt-1">
              Daily Active: {analytics.dailyActiveUsers}
            </p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="body-text text-sm text-gray-600 mb-1">Total Analyses</p>
            <p className="h1 text-4xl text-blazerex-green">{analytics.totalAnalyses}</p>
            <p className="body-text text-xs text-gray-500 mt-1">
              Transcripts: {analytics.totalTranscripts} • Filings: {analytics.totalFilings}
            </p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="body-text text-sm text-gray-600 mb-1">Credits Used</p>
            <p className="h1 text-4xl text-blazerex-green">{analytics.totalCreditsUsed.toLocaleString()}</p>
            <p className="body-text text-xs text-gray-500 mt-1">
              {analytics.totalCreditTransactions} transactions
            </p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="body-text text-sm text-gray-600 mb-1">Est. OpenAI Cost</p>
            <p className="h1 text-4xl text-blazerex-green">${analytics.estimatedOpenAICost.toFixed(2)}</p>
            <p className="body-text text-xs text-gray-500 mt-1">
              {timeRange === '7d' ? 'Last 7 days' : timeRange === '30d' ? 'Last 30 days' : 'All time'}
            </p>
          </div>
        </Card>
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <div className="text-center">
            <p className="body-text text-sm text-gray-600 mb-1">News Articles</p>
            <p className="h1 text-3xl text-blazerex-green">{analytics.totalNews}</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="body-text text-sm text-gray-600 mb-1">Rankings</p>
            <p className="h1 text-3xl text-blazerex-green">{analytics.totalRankings}</p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="body-text text-sm text-gray-600 mb-1">Daily Active Users</p>
            <p className="h1 text-3xl text-blazerex-green">{analytics.dailyActiveUsers}</p>
          </div>
        </Card>
      </div>

      {/* Most Analyzed Companies */}
      {analytics.mostAnalyzedCompanies.length > 0 && (
        <Card>
          <h3 className="sub-header text-xl mb-4">Most Analyzed Companies</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-4 body-text text-sm text-gray-600 font-medium">Rank</th>
                  <th className="text-left py-2 px-4 body-text text-sm text-gray-600 font-medium">Company</th>
                  <th className="text-right py-2 px-4 body-text text-sm text-gray-600 font-medium">Analyses</th>
                </tr>
              </thead>
              <tbody>
                {analytics.mostAnalyzedCompanies.map((company, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <span className="body-text font-mono text-gray-500">#{index + 1}</span>
                    </td>
                    <td className="py-3 px-4">
                      <p className="body-text font-medium">{company.companyName}</p>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="body-text text-blazerex-green font-medium">{company.count}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Top Transcripts */}
      {analytics.topTranscripts.length > 0 && (
        <Card>
          <h3 className="sub-header text-xl mb-4">Top Transcripts by Analysis</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-4 body-text text-sm text-gray-600 font-medium">Rank</th>
                  <th className="text-left py-2 px-4 body-text text-sm text-gray-600 font-medium">Company</th>
                  <th className="text-left py-2 px-4 body-text text-sm text-gray-600 font-medium">Quarter</th>
                  <th className="text-right py-2 px-4 body-text text-sm text-gray-600 font-medium">Analyses</th>
                </tr>
              </thead>
              <tbody>
                {analytics.topTranscripts.map((transcript, index) => (
                  <tr key={transcript.transcriptId} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <span className="body-text font-mono text-gray-500">#{index + 1}</span>
                    </td>
                    <td className="py-3 px-4">
                      <p className="body-text font-medium">{transcript.companyName}</p>
                    </td>
                    <td className="py-3 px-4">
                      <p className="body-text text-gray-600">{transcript.quarter}</p>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="body-text text-blazerex-green font-medium">{transcript.count}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Recent Activity */}
      <Card>
        <h3 className="sub-header text-xl mb-4">Recent System Activity</h3>
        <div className="space-y-2">
          {analytics.recentActivity.length === 0 ? (
            <p className="body-text text-gray-500">No recent activity</p>
          ) : (
            analytics.recentActivity.map((activity, i) => (
              <div key={i} className="flex items-center justify-between border-b border-gray-100 pb-2 last:border-0">
                <div>
                  <p className="body-text text-sm">{activity.description}</p>
                  <p className="body-text text-xs text-gray-500">
                    {new Date(activity.timestamp?.toDate?.() || activity.timestamp).toLocaleString()}
                  </p>
                </div>
                <span className={`px-2 py-1 rounded text-xs ${
                  activity.type === 'error' ? 'bg-red-100 text-red-800' :
                  activity.type === 'info' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {activity.type}
                </span>
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  );
};

